import { gql } from "urql";
import type { TypedDocumentNode } from "urql";
import { Kpi } from "@acto/taskpane/core/domain";
import type { ClientOrderBy, ClientTrendFilter, KpiComparisonMode } from "@acto/gql/graphql";
import { useComparisonModeContext } from "@acto/taskpane/core/contexts/TenantSettingsContext";
import { toGqlKpiComparisonMode } from "@acto/taskpane/core/data-access/customers/kpiTransformers";
import { usePagination } from "@acto/taskpane/core/data-access/pagination/usePagination";
import type { PaginatedDataAccessResult } from "@acto/taskpane/core/data-access/types";
import { orderByForKpi } from "@acto/taskpane/field-sales-2/data-access/useTenantClientsByKpi";
import type { CustomerTrendSource } from "@acto/taskpane/field-sales-2/data-access/customerTrendItem";
import { type CorporateGroupEnvelope, firstCorporateGroup } from "./corporateGroupEnvelope";

const PAGE_SIZE = 10;

interface MemberKpi {
  totalValue: number;
  previousTotalValue: number;
  percentageChange: number | null;
}

interface MemberNode {
  id: string;
  name: string;
  externalReference: string | null;
  revenue: MemberKpi | null;
  profit: MemberKpi | null;
  orderCount: MemberKpi | null;
  margin: MemberKpi | null;
}

interface MemberEdge {
  node: MemberNode;
}

type GroupMembersQueryData = CorporateGroupEnvelope<{
  id: string;
  members: { edges: MemberEdge[]; pageInfo: { hasNextPage: boolean; endCursor: string | null } };
}>;

interface GroupMembersQueryVariables {
  id: string;
  first: number;
  after?: string | null;
  orderBy: ClientOrderBy;
  dateRange: { startDate: string; endDate: string };
  searchTerm?: string | null;
  trends?: ClientTrendFilter[] | null;
  comparisonMode: KpiComparisonMode;
}

const GroupMembersQuery: TypedDocumentNode<GroupMembersQueryData, GroupMembersQueryVariables> = gql`
  query GetCorporateGroupMembers(
    $id: ID!
    $first: Int!
    $after: String
    $orderBy: ClientOrderBy
    $dateRange: DateRangeInput
    $searchTerm: String
    $trends: [ClientTrendFilter!]
    $comparisonMode: KpiComparisonMode!
  ) {
    clients(first: 1, filter: { id: $id }) {
      edges {
        node {
          id
          corporateGroup {
            id
            members(
              first: $first
              after: $after
              filter: {
                orderBy: $orderBy
                dateRange: $dateRange
                searchTerm: $searchTerm
                trends: $trends
                searchMode: SMART
                comparisonMode: $comparisonMode
              }
            ) {
              edges {
                node {
                  id
                  name
                  externalReference
                  revenue(dateRange: $dateRange, comparisonMode: $comparisonMode) {
                    totalValue
                    previousTotalValue
                    percentageChange
                  }
                  profit(dateRange: $dateRange, comparisonMode: $comparisonMode) {
                    totalValue
                    previousTotalValue
                    percentageChange
                  }
                  orderCount(dateRange: $dateRange, comparisonMode: $comparisonMode) {
                    totalValue
                    previousTotalValue
                    percentageChange
                  }
                  margin(dateRange: $dateRange, comparisonMode: $comparisonMode) {
                    totalValue
                    previousTotalValue
                    percentageChange
                  }
                }
              }
              pageInfo {
                hasNextPage
                endCursor
              }
            }
          }
        }
      }
    }
  }
`;

const EMPTY_CONNECTION = {
  edges: [],
  pageInfo: { hasNextPage: false, endCursor: null },
};

export interface GroupMemberRow extends CustomerTrendSource {
  id: string;
}

interface GroupMembersOptions {
  clientId: string | undefined;
  selectedKpi: Kpi;
  dateRange: { startDate: string; endDate: string };
  searchTerm?: string;
  trends?: ClientTrendFilter[];
}

/** Ranked, keyset-paginated members of the client's corporate group, with per-row KPI values. */
export function useGroupMembers({
  clientId,
  selectedKpi,
  dateRange,
  searchTerm,
  trends,
}: GroupMembersOptions): PaginatedDataAccessResult<GroupMemberRow> {
  const trimmedSearchTerm = searchTerm?.trim() ?? "";
  const comparisonMode = toGqlKpiComparisonMode(useComparisonModeContext());

  const variables = {
    id: clientId ?? "",
    comparisonMode,
    orderBy: orderByForKpi(selectedKpi),
    dateRange,
    searchTerm: trimmedSearchTerm.length > 0 ? trimmedSearchTerm : null,
    trends: trends?.length ? trends : null,
  };

  const { data, isFetching, isPending, error, hasNextPage, fetchNextPage, refreshAfter } = usePagination({
    query: GroupMembersQuery,
    limit: PAGE_SIZE,
    variables,
    enabled: !!clientId,
    connection: (queryData) => {
      const members = firstCorporateGroup(queryData)?.members ?? EMPTY_CONNECTION;
      return {
        edges: members.edges.map((edge) => ({ node: toGroupMemberRow(edge) })),
        pageInfo: members.pageInfo,
      };
    },
  });

  return { data, isFetching, isPending, error, hasNextPage, fetchNextPage, refreshAfter };
}

function toGroupMemberRow({ node }: MemberEdge): GroupMemberRow {
  return {
    id: node.id,
    name: node.name || (node.externalReference ?? ""),
    externalReference: node.externalReference,
    revenueKpi: node.revenue ?? undefined,
    profitKpi: node.profit ?? undefined,
    ordersKpi: node.orderCount ?? undefined,
    marginKpi: node.margin ?? undefined,
  };
}
